import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["currency", "salaryMin", "salaryMax", "salaryOffered"]

  connect() {
    this.updateCurrencySymbols()
    this.setupInputListeners()
    this.normalizeInitialValues()
  }

  setupInputListeners() {
    // Add input event listeners to maintain currency symbols
    if (this.hasSalaryMinTarget) {
      this.salaryMinTarget.addEventListener('input', (e) => this.handleInput(e))
    }

    if (this.hasSalaryMaxTarget) {
      this.salaryMaxTarget.addEventListener('input', (e) => this.handleInput(e))
    }

    if (this.hasSalaryOfferedTarget) {
      this.salaryOfferedTarget.addEventListener('input', (e) => this.handleInput(e))
    }
  }

  updateCurrencySymbols() {
    const currency = this.currencyTarget.value
    const symbol = this.getCurrencySymbol(currency)

    // Update all salary input fields
    if (this.hasSalaryMinTarget) {
      this.updateInputSymbol(this.salaryMinTarget, symbol)
    }

    if (this.hasSalaryMaxTarget) {
      this.updateInputSymbol(this.salaryMaxTarget, symbol)
    }

    if (this.hasSalaryOfferedTarget) {
      this.updateInputSymbol(this.salaryOfferedTarget, symbol)
    }
  }

  getCurrencySymbol(currency) {
    const symbols = {
      'USD': '$',
      'CAD': 'C$'
    }
    return symbols[currency] || currency
  }

  updateInputSymbol(inputElement, symbol) {
    // Update the placeholder to show the symbol
    inputElement.placeholder = `${symbol} Amount`

    // Also update the label if it exists
    const label = inputElement.previousElementSibling
    if (label && label.tagName === 'LABEL') {
      // Preserve the original label text to avoid chopping the first letter
      if (!label.dataset.originalLabel) {
        label.dataset.originalLabel = label.textContent.trim()
      }
      const originalText = label.dataset.originalLabel
      label.textContent = `${symbol} ${originalText}`
    }

    // Don't set the value to just the symbol for number inputs
    // Instead, just update the placeholder and label
  }

  handleInput(event) {
    const input = event.target
    const currency = this.currencyTarget.value
    const symbol = this.getCurrencySymbol(currency)

    // Enforce whole-dollar amounts (no decimals) and numeric-only
    // Strip all non-digits
    const digitsOnly = input.value.replace(/[^0-9]/g, '')
    input.value = digitsOnly

    // Ensure the placeholder shows the symbol (value remains numeric-only)
    input.placeholder = `${symbol} Amount`
  }

  // Normalize any prefilled values (e.g., 50000.0) to whole numbers on load
  normalizeInitialValues() {
    const normalize = (el) => {
      if (!el) return
      if (el.value == null) return
      // Remove everything except digits
      const digitsOnly = String(el.value).replace(/[^0-9]/g, '')
      el.value = digitsOnly
    }

    if (this.hasSalaryMinTarget) normalize(this.salaryMinTarget)
    if (this.hasSalaryMaxTarget) normalize(this.salaryMaxTarget)
    if (this.hasSalaryOfferedTarget) normalize(this.salaryOfferedTarget)
  }

  // Handle currency change
  currencyChanged() {
    this.updateCurrencySymbols()
  }

  // Clean currency symbols before form submission
  beforeSubmit() {
    const currency = this.currencyTarget.value
    const symbol = this.getCurrencySymbol(currency)

    if (this.hasSalaryMinTarget) {
      this.cleanInputValue(this.salaryMinTarget, symbol)
    }

    if (this.hasSalaryMaxTarget) {
      this.cleanInputValue(this.salaryMaxTarget, symbol)
    }

    if (this.hasSalaryOfferedTarget) {
      this.cleanInputValue(this.salaryOfferedTarget, symbol)
    }
  }

  cleanInputValue(inputElement, symbol) {
    const value = inputElement.value
    if (value && value.startsWith(symbol)) {
      // Remove the currency symbol for form submission
      inputElement.value = value.substring(symbol.length)
    }
  }
}
